<template>
  <div
    v-if="transfer"
    class="rounded-lg bg-white/60 p-3 lg:px-4 lg:py-2"
    data-testid="timeline-transfer"
  >
    <div class="flex flex-col items-center justify-between lg:flex-row">
      <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-8">
        <div class="flex items-center gap-4">
          <span class="max-w-fit rounded-lg bg-[#f5f1aa] px-4 py-1 text-sm font-medium">
            {{ t('transfer') }}
          </span>
          <span class="font-medium">{{
            t(transfer.type === AllocationType.TRANSFER_DELIVERY ? 'delivery' : 'return')
          }}</span>
        </div>

        <div class="flex flex-col gap-1">
          <div class="flex flex-row gap-2">
            <span
              class="flex flex-col items-start gap-2 text-obsidian-500 md:flex-row md:items-center"
            >
              <span class="flex items-center gap-2">
                <ArrowRightFromLineIcon class="size-4 shrink-0 text-hazel-500 md:hidden" />
                <AllocationStationSelect
                  v-if="originStationMode === 'pickup'"
                  :allocation="transfer"
                  :booking-id="bookingId"
                  mode="pickup"
                  :confirm-change-location-label="t('pickupLocation')"
                  :exclude-station-id="excludeStationId"
                  :home-station-id="carStation.id"
                >
                  {{ getLocationText(transfer, 'pickup', locale) }}
                </AllocationStationSelect>
                <span v-else>{{ getLocationText(transfer, 'pickup', locale) }}</span>
              </span>
              <ArrowRightIcon class="hidden size-4 shrink-0 md:block" />
              <span class="flex items-center gap-2">
                <ArrowLeftToLineIcon class="size-4 shrink-0 text-hazel-500 md:hidden" />
                <AllocationStationSelect
                  v-if="originStationMode === 'dropoff'"
                  :allocation="transfer"
                  :booking-id="bookingId"
                  mode="dropoff"
                  :confirm-change-location-label="t('dropoffLocation')"
                  :exclude-station-id="excludeStationId"
                  :home-station-id="carStation.id"
                >
                  {{ getLocationText(transfer, 'dropoff', locale) }}
                </AllocationStationSelect>
                <span v-else>{{ getLocationText(transfer, 'dropoff', locale) }}</span>
              </span>
            </span>
          </div>
          <div class="flex">
            <span class="mr-2">{{ t('driver') }}:</span>
            <AllocationDriverSelect :allocation="transfer" :booking-id="bookingId" />
          </div>
        </div>
      </div>
      <div v-if="canBeEdited" class="flex items-center justify-end self-stretch">
        <CVButton
          variant="ghost"
          size="icon"
          :aria-label="t('deleteTransfer')"
          :title="t('deleteTransfer')"
          @click="$emit('delete', transfer)"
          ><TrashIcon class="h-4 w-4 text-red-500"
        /></CVButton>
        <CVButton
          variant="ghost"
          size="icon"
          :aria-label="t('editTransfer')"
          :title="t('editTransfer')"
          data-testid="edit-transfer-button"
          @click="editTransfer"
          ><PencilIcon class="h-4 w-4"
        /></CVButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import router from '@/router';
import { AllocationStatus, AllocationType } from '@carvia/ros-client-types';
import {
  ArrowLeftToLineIcon,
  ArrowRightFromLineIcon,
  ArrowRightIcon,
  PencilIcon,
  TrashIcon,
} from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import CVButton from '../buttons/CVButton.vue';
import { computed } from 'vue';
import { getStationOfCarByDate } from '@/hooks/use-current-station-of-car.ts';
import AllocationDriverSelect from './AllocationDriverSelect.vue';
import AllocationStationSelect from './AllocationStationSelect.vue';
import { getLocationText } from '@/utils/get-location-text.ts';

const { t, locale } = useI18n();

const props = defineProps<{
  allocation: Allocation;
  bookingId: string;
  type?: AllocationType;
}>();

defineEmits<{
  (e: 'delete', transfer: Allocation): void;
}>();

const transfer = computed(() => {
  return props.allocation.transfers?.find((t) => t.type === props.type);
});

const canBeEdited = computed(() => {
  return (
    ![AllocationStatus.HANDED_OVER, AllocationStatus.RETURNED].includes(props.allocation.status) ||
    (props.type === AllocationType.TRANSFER_RETURN &&
      props.allocation.status !== AllocationStatus.RETURNED)
  );
});

const editTransfer = () => {
  if (!transfer.value) return;
  router.push({
    name: 'transferEdit',
    params: {
      transferType: props.type,
      allocationId: props.allocation.id,
      transferId: transfer.value.id,
    },
  });
};

const originStationMode = props.type === AllocationType.TRANSFER_DELIVERY ? 'pickup' : 'dropoff';
const reverseStationMode = props.type === AllocationType.TRANSFER_DELIVERY ? 'dropoff' : 'pickup';

const carStation = computed(() =>
  getStationOfCarByDate(props.allocation.car, props.allocation.startDate),
);

const excludeStationId = computed(() => {
  if (!transfer.value) return null;
  if (transfer.value[`${reverseStationMode}LocationType`] !== BookingLocationType.STATION) {
    return null;
  }
  return transfer.value[`${reverseStationMode}ResponsibleStation`]?.id ?? null;
});
</script>

<i18n lang="json">
{
  "en": {
    "delivery": "Delivery",
    "return": "Return",
    "transfer": "Transfer",
    "deleteTransfer": "Delete transfer",
    "editTransfer": "Edit transfer",
    "driver": "Driver",
    "pickupLocation": "transfer start location",
    "dropoffLocation": "transfer destination location"
  },
  "de": {
    "delivery": "Anlieferung",
    "return": "Abholung",
    "transfer": "Transfer",
    "deleteTransfer": "Transfer löschen",
    "editTransfer": "Transfer bearbeiten",
    "driver": "Fahrer",
    "pickupLocation": "Transferstartort",
    "dropoffLocation": "Transferzielort"
  },
  "es": {
    "delivery": "Entrega",
    "return": "Recogida",
    "transfer": "Traslado",
    "deleteTransfer": "Eliminar traslado",
    "editTransfer": "Editar traslado",
    "driver": "Conductor",
    "pickupLocation": "lugar de inicio del traslado",
    "dropoffLocation": "lugar de destino del traslado"
  }
}
</i18n>
